import { useEffect } from "react";

const DEFAULT_TITLE = "California Corporate Chaplains | Workplace Chaplaincy";
const DEFAULT_DESCRIPTION =
  "California Corporate Chaplains (CCCHAPS) provides relational, non-clinical workplace chaplaincy — quiet, confidential workforce support for California employers, HR leaders, and executives.";
const CANONICAL_HOST = "https://ccchaps.com";
const CANONICAL_ID = "site-canonical-link";
const ROBOTS_ID = "site-robots-meta";

function setMeta(name, content, attr = "name") {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href) {
  if (!href) return;
  let link = document.getElementById(CANONICAL_ID);
  if (!link) {
    link = document.createElement("link");
    link.id = CANONICAL_ID;
    link.rel = "canonical";
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

function clearCanonical() {
  const link = document.getElementById(CANONICAL_ID);
  if (link) link.remove();
}

function setRobots(directive) {
  let el = document.getElementById(ROBOTS_ID);
  if (!directive) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("meta");
    el.id = ROBOTS_ID;
    el.setAttribute("name", "robots");
    document.head.appendChild(el);
  }
  el.setAttribute("content", directive);
}

/**
 * Sets document.title, meta description, Open Graph / Twitter tags,
 * <link rel="canonical">, and (optionally) a robots directive.
 * Restores defaults on unmount.
 *
 * @param {Object} opts
 * @param {string} [opts.title]
 * @param {string} [opts.description]
 * @param {string} [opts.canonicalPath] - Optional explicit path override (e.g. "/services"); defaults to window.location.pathname
 * @param {boolean} [opts.noindex]      - When true, injects <meta name="robots" content="noindex,follow">
 */
export default function usePageMeta({ title, description, canonicalPath, noindex } = {}) {
  useEffect(() => {
    const fullTitle = title || DEFAULT_TITLE;
    const fullDesc = description || DEFAULT_DESCRIPTION;
    document.title = fullTitle;
    setMeta("description", fullDesc);
    setMeta("og:title", fullTitle, "property");
    setMeta("og:description", fullDesc, "property");
    setMeta("twitter:title", fullTitle);
    setMeta("twitter:description", fullDesc);

    const pathname = canonicalPath || (typeof window !== "undefined" ? window.location.pathname : "/");
    const canonicalHref = `${CANONICAL_HOST}${pathname}`;
    setCanonical(canonicalHref);
    setMeta("og:url", canonicalHref, "property");

    setRobots(noindex ? "noindex,follow" : null);

    return () => {
      document.title = DEFAULT_TITLE;
      setMeta("description", DEFAULT_DESCRIPTION);
      setMeta("og:title", DEFAULT_TITLE, "property");
      setMeta("og:description", DEFAULT_DESCRIPTION, "property");
      setMeta("twitter:title", DEFAULT_TITLE);
      setMeta("twitter:description", DEFAULT_DESCRIPTION);
      clearCanonical();
      setRobots(null);
    };
  }, [title, description, canonicalPath, noindex]);
}
